import { computeToolMode } from '../../permissions/modes';
import { useFileManagerPermissions } from '../../permissions/useFileManagerPermissions';
import type { FileSystemItem } from '../../types';
import type { FileManagerTool } from './fileManagerTool';
import { TOOL_FACTORIES } from './registry';

/**
 * The tools available for a given context-menu target: structurally fit AND permitted.
 *
 * Self-injects `useFileManagerPermissions()` rather than taking it as a param — like
 * useFileGridInteractions and unlike the composables FileManager.vue's own setup() calls, because every
 * caller of this is a genuine descendant of the component that provides the mounts. Encapsulating the
 * permission read here is what lets useDirectoryTreeContextMenu, which does not inject permissions at
 * all, adopt tools with no new plumbing.
 *
 * Must be called during setup(): the factories build tools whose `run` closes over inject()ed state, and
 * they are called once here for exactly that reason. `toolsFor` is then a plain function the event
 * handler can call whenever it learns its target.
 */
export const useFileManagerTools = () => {
  const { permissionsAt } = useFileManagerPermissions();
  const tools = TOOL_FACTORIES.map((factory) => factory());

  const toolsFor = (target: FileSystemItem[]): FileManagerTool[] =>
    tools.filter(
      (tool) =>
        tool.isAvailable(target) &&
        // `every`, not `some`: a selection can span mounts, and one file from a mount that does not grant
        // the permission hides the tool entirely rather than letting it half-work — running on the
        // permitted subset and silently dropping the rest would be worse than not offering it.
        target.every(
          (item) =>
            computeToolMode(permissionsAt(item.path), tool.permission) === 'enabled',
        ),
    );

  return { toolsFor };
};
